import React, { useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Box, CircularProgress, Fade } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import IconButton from "@mui/material/IconButton";
import LeftArrowIcon from "@mui/icons-material/ArrowLeft";
import RightArrowIcon from "@mui/icons-material/ArrowRight";
import { DatePicker } from "@mui/x-date-pickers";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import DeleteIcon from "@mui/icons-material/Delete";
import Typography from "@mui/material/Typography";
import queryString from "query-string";
import dayjs from "dayjs";
import { useRecipeManager } from "../../context-providers/recipe-manager-provider";
import IngredientList from "../ingredient/ingredient-list";
import TitleBar from "../nav/title-bar";
import { useMealPlanner } from "../../context-providers/meal-planner-provider";
import IntentButton, { Intent } from "../input/intent-button";
import RecipeField from "../recipe/recipe-field";
import ROUTES from "../../routes/routes";
import RecipeMethod from "../recipe/recipe-method";
import { DEFAULT_FADE_ANIMATION } from "../../constants";
import ConfirmDeleteDialog from "../dialog/confirm-delete-dialog";
import { useFriendships } from "../../context-providers/friendships-provider";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
  },

  buttons: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-evenly",
    position: "sticky",
    zIndex: 10,
    [theme.breakpoints.down("sm")]: {
      top: "54px",
    },
    [theme.breakpoints.up("sm")]: {
      top: "64px",
    },
  },

  arrowButton: {
    padding: "0 12px",
  },

  button: {
    margin: "5px 0",
  },

  method: {
    marginTop: "4px",
    padding: "4px",
    whiteSpace: "pre-wrap",
    width: "100%",
    minHeight: "30px",
  },

  datePicker: {
    marginLeft: "10px",
    width: "120px",
  },

  emptyText: {
    color: theme.palette.grey.A200,
    padding: "10px 0",
  },

  notes: {
    marginTop: "4px",
    padding: "4px",
    whiteSpace: "pre-wrap",
    width: "100%",
    minHeight: "30px",
  },
}));

export default function Meal() {
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    getUserMeal,
    getHomeMeal,
    saveUserMeal,
    saveHomeMeal,
    removeUserMeal,
    removeHomeMeal,
  } = useMealPlanner();
  const { mealId } = useParams();
  const { friends } = useFriendships();
  const { getHomeRecipe, getUserRecipe } = useRecipeManager();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const isHome = queryString.parse(location.search).isHome === "true";

  const getMeal = isHome ? getHomeMeal : getUserMeal;
  const saveMeal = isHome ? saveHomeMeal : saveUserMeal;
  const removeMeal = isHome ? removeHomeMeal : removeUserMeal;

  const meal = getMeal(mealId);

  // Show loading state while data is being fetched
  if (!meal) {
    return (
      <Box className={classes.root}>
        <TitleBar title="Loading..." onBack={() => navigate(ROUTES.PLANNER)} />
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }
  const ingredientMultiplier = meal.serves / meal.originalServes;

  function handleMoreServings() {
    saveMeal({ ...meal, serves: meal.serves + 1 });
  }

  function handleLessServings() {
    if (meal.serves > 1) {
      saveMeal({ ...meal, serves: meal.serves - 1 });
    }
  }

  function handleRemove() {
    removeMeal(mealId);
    navigate(ROUTES.PLANNER);
  }

  function findOriginalRecipeLocation() {
    if (getUserRecipe(meal?.recipeId)) {
      return {
        pathname: `${ROUTES.RECIPES}/${meal.recipeId}`,
      };
    }
    if (getHomeRecipe(meal?.recipeId)) {
      return {
        pathname: `${ROUTES.RECIPES}/${meal.recipeId}`,
        search: "isHome=true&homeRecipe=true",
      };
    }
    if (meal?.sharedFrom) {
      if (friends?.find((f: any) => f.username === meal.sharedFrom)) {
        return {
          pathname: `${ROUTES.FRIENDS}/${meal.sharedFrom}/${meal.recipeId}`,
        };
      }
    }
  }

  const originalRecipeLocation = findOriginalRecipeLocation();

  return (
    <Box className={classes.root}>
      <TitleBar title={meal.name} onBack={() => navigate(ROUTES.PLANNER)} />
      <Fade in timeout={DEFAULT_FADE_ANIMATION}>
        <Box className={classes.root}>
          <Box className={classes.buttons}>
            {originalRecipeLocation && (
              <IntentButton
                className={classes.button}
                onClick={() => navigate(originalRecipeLocation)}
                variant="contained"
                intent={Intent.PRIMARY}
                startIcon={<RestaurantIcon />}
              >
                Go to Recipe
              </IntentButton>
            )}
            <IntentButton
              className={classes.button}
              onClick={() => setShowDeleteDialog(true)}
              variant="contained"
              intent={Intent.ERROR}
              startIcon={<DeleteIcon />}
            >
              Delete
            </IntentButton>
          </Box>
          <RecipeField name="Servings" inline>
            <Box>
              <IconButton
                className={classes.arrowButton}
                disabled={meal.serves <= 1}
                onClick={handleLessServings}
              >
                <LeftArrowIcon />
              </IconButton>
              {meal.serves}
              <IconButton
                className={classes.arrowButton}
                onClick={handleMoreServings}
              >
                <RightArrowIcon />
              </IconButton>
            </Box>
          </RecipeField>
          <RecipeField name="Date" inline>
            <DatePicker
              className={classes.datePicker}
              label=""
              format="DD MMM YYYY"
              value={meal.date ? dayjs(meal.date) : null}
              onChange={(date: any) =>
                saveMeal({ ...meal, date: date.toDate() })
              }
              disablePast
            />
          </RecipeField>
          <RecipeField name="Ingredients">
            <IngredientList
              multiplier={ingredientMultiplier}
              ingredients={meal.ingredients}
              editMode={false}
            />
          </RecipeField>
          <RecipeField name="Method">
            <RecipeMethod method={meal.method} />
          </RecipeField>
          <RecipeField name="Notes">
            <br />
            <Typography
              className={cx(classes.notes, {
                [classes.emptyText]: !meal.notes,
              })}
              variant="body1"
              align="center"
            >
              {meal.notes ? meal.notes : "No Notes"}
            </Typography>
          </RecipeField>
          <ConfirmDeleteDialog
            open={showDeleteDialog}
            handleClose={() => setShowDeleteDialog(false)}
            item={meal}
            onDelete={handleRemove}
            idProp="mealId"
            itemType="Meal"
          />
          <Box id="bottom-shim" style={{ height: "90px" }} />
        </Box>
      </Fade>
    </Box>
  );
}
